import RouteError from '@/components/ui/route-error';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { WHOLESALER_SETTINGS_TABS } from '@/types/route-tabs';
import { LuShieldCheck, LuSettings2 } from 'react-icons/lu';
import { Tabs } from '@/components/ui/lib/tabs';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { queryClient } from '@/lib/query-client';
import { DICTIONARY } from '@/config/dictionary';
import { requireAuth } from '@/utils/require-auth-before-load';
import { useAppStore } from '@/stores/app-store';
import KycTab from '@/features/payments/components/kyc-tab';
import TcapSettingsTab from '@/features/payments/components/tcap-settings-tab';
import { getWholesalerCurrentQueryOptions } from '@/features/payments/api/wholesaler-current-api';

const wholesalerSettingsSearchSchema = z.object({
  tab: z.enum(WHOLESALER_SETTINGS_TABS).optional(),
});

const { wholesalerSettings } = DICTIONARY.dashboard;

const DashboardWholesalerSettingsPage = () => {
  const { tab } = useSearch({ from: '/wholesaler-settings' });

  return (
    <DashboardRouteLayout>
      <DashboardPageShell
        title={wholesalerSettings.pageTitle}
        description={wholesalerSettings.pageDescription}
      >
        <Tabs.Root
          defaultValue={tab ?? WHOLESALER_SETTINGS_TABS[0]}
          variant="line"
          lazyMount
          unmountOnExit
        >
          <Tabs.List mb="6">
            <Tabs.Trigger value={WHOLESALER_SETTINGS_TABS[0]}>
              <LuShieldCheck />
              {wholesalerSettings.tabs.kyc}
            </Tabs.Trigger>
            <Tabs.Trigger value={WHOLESALER_SETTINGS_TABS[1]}>
              <LuSettings2 />
              {wholesalerSettings.tabs.tcapSettings}
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value={WHOLESALER_SETTINGS_TABS[0]}>
            <KycTab />
          </Tabs.Content>

          <Tabs.Content value={WHOLESALER_SETTINGS_TABS[1]}>
            <TcapSettingsTab />
          </Tabs.Content>
        </Tabs.Root>
      </DashboardPageShell>
    </DashboardRouteLayout>
  );
};

export const Route = createFileRoute('/wholesaler-settings')({
  validateSearch: wholesalerSettingsSearchSchema,
  beforeLoad: requireAuth,
  loader: async ({ location }) => {
    const params = new URLSearchParams(location.search);
    const activeTab = (params.get('tab') ??
      WHOLESALER_SETTINGS_TABS[0]) as (typeof WHOLESALER_SETTINGS_TABS)[number];

    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerId = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id;

    const prefetchByTab: Record<(typeof WHOLESALER_SETTINGS_TABS)[number], () => Promise<unknown>> =
      {
        kyc: () => Promise.resolve(),
        'tcap-settings': () =>
          wholesalerId == null
            ? Promise.resolve()
            : queryClient.prefetchQuery(getWholesalerCurrentQueryOptions(wholesalerId)),
      };

    await prefetchByTab[activeTab]();
    return null;
  },
  component: DashboardWholesalerSettingsPage,
  errorComponent: RouteError,
});
